import { useState } from 'react';
import { PageShell } from '../components/PageShell';
import { GlassPanel } from '../components/GlassPanel';
import { TextInput, SelectField } from '../components/forms';
import { MaterialIcon } from '../components/MaterialIcon';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { useGame } from '../state/gameContext';



export default function CreateRoomPage() {
  const { createRoom } = useGame();
  const [roomName, setRoomName] = useState('Midnight Chaos');
  const [nickname, setNickname] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(8);
  const [pointsToWin, setPointsToWin] = useState(5);
  const [wildEnabled, setWildEnabled] = useState(true);

  const handleCreate = () => {
    createRoom(roomName, nickname || 'Host', {
      maxPlayers,
      pointsToWin,
      wildCardsEnabled: wildEnabled,
    });
  };

  return (
    <PageShell mainClassName="pt-24 pb-32 px-4 md:px-margin-desktop flex flex-col items-center max-w-4xl mx-auto min-h-screen">
      <div className="w-full mb-stack-lg text-center md:text-left">
        <h1 className="font-headline-md text-headline-md text-primary mb-2">Create New Lobby</h1>
        <p className="text-on-surface-variant max-w-lg">
          Configure your game room. You&apos;ll receive a unique code and invite link once created.
        </p>
      </div>

      <form className="w-full grid grid-cols-1 md:grid-cols-12 gap-gutter" onSubmit={(e) => e.preventDefault()}>
        <GlassPanel className="md:col-span-12 p-stack-lg rounded-xl flex flex-col gap-stack-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-stack-lg">
            <TextInput
              label="Room Name"
              placeholder="e.g., Midnight Chaos"
              icon="edit"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
            />
            <TextInput
              label="Your Player Nickname"
              placeholder="The Joker"
              icon="person"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
            />
          </div>
        </GlassPanel>

        <GlassPanel className="md:col-span-8 p-stack-lg rounded-xl flex flex-col gap-stack-lg">
          <h2 className="font-label-bold text-label-bold flex items-center gap-2 text-on-surface">
            <MaterialIcon name="tune" className="text-secondary" />
            Gameplay Settings
          </h2>
          <div className="grid grid-cols-1 gap-stack-lg">
            <div className="flex flex-col gap-stack-sm">
              <div className="flex justify-between items-center">
                <label htmlFor="max-players" className="text-on-surface-variant">
                  Max Players
                </label>
                <span className="text-primary font-bold">{maxPlayers} Players</span>
              </div>
              <input
                id="max-players"
                type="range"
                min={3}
                max={12}
                step={1}
                value={maxPlayers}
                onChange={(e) => setMaxPlayers(Number(e.target.value))}
                className="w-full h-2 bg-surface-container-highest rounded-full appearance-none cursor-pointer custom-slider"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-stack-md">
              <SelectField label="Points to Win" value={pointsToWin} onChange={(e) => setPointsToWin(Number(e.target.value))}>
                <option value={3}>3 Points (Quick demo)</option>
                <option value={5}>5 Points (Quick)</option>
                <option value={10}>10 Points (Standard)</option>
                <option value={15}>15 Points (Endurance)</option>
              </SelectField>
              <p className="text-sm text-on-surface-variant self-center">English card deck</p>
            </div>
            <div className="flex flex-col gap-4 border-t border-white/5 pt-stack-md">
              <p className="text-sm text-on-surface-variant">Untimed rounds. Take a moment to find your answer.</p>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <label htmlFor="wild-toggle" className="text-on-surface">
                    Allow write-in wild cards
                  </label>
                  <span className="text-xs text-on-surface-variant">Players can write their own answers</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    id="wild-toggle"
                    type="checkbox"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer peer z-10"
                    checked={wildEnabled}
                    onChange={(e) => setWildEnabled(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-surface-container-highest peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary" />
                </label>
              </div>
            </div>

          </div>
        </GlassPanel>

        <div className="md:col-span-4 flex flex-col gap-gutter">
          <GlassPanel className="p-stack-lg rounded-xl flex flex-col gap-stack-md">
            <p className="font-bold">Invite your friends</p><p className="text-sm text-on-surface-variant">Anyone with your room code can join. Share it with the people you want at your table.</p>
          </GlassPanel>
          <GlassPanel className="p-stack-lg rounded-xl flex flex-col gap-stack-md">
            <button
              type="button"
              onClick={handleCreate}
              data-testid="create-room"
              className="w-full bg-primary hover:bg-primary-container text-on-primary font-bold py-4 rounded-xl shadow-[0_0_20px_rgba(221,183,255,0.3)] transition-all active:scale-95 duration-100 flex items-center justify-center gap-2 text-card-text"
            >
              Create Game
              <MaterialIcon name="rocket_launch" />
            </button>
          </GlassPanel>
        </div>
      </form>
      <MobileBottomNav active="/create" />
    </PageShell>
  );
}
